import React, { useState, useEffect, useCallback, useRef } from 'react';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import { toast } from 'react-toastify';
import { FaDatabase, FaUpload, FaSearch, FaPlus, FaTimes, FaTrash } from 'react-icons/fa';
import './KnowledgeBase.css';

const KnowledgeBase = () => {
  const { user } = useAuth();
  const [tab, setTab] = useState('workspaces');
  const [workspaces, setWorkspaces] = useState([]);
  const [selectedWs, setSelectedWs] = useState('');
  const [ragStatus, setRagStatus] = useState(null);
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadMsg, setUploadMsg] = useState('');
  const [question, setQuestion] = useState('');
  const [answer, setAnswer] = useState(null);
  const [querying, setQuerying] = useState(false);
  const [newWsName, setNewWsName] = useState('');
  const [newWsDesc, setNewWsDesc] = useState('');
  const fileInputRef = useRef(null);
  const dropRef = useRef(null);

  const loadWorkspaces = useCallback(async () => {
    try {
      const res = await api.get('/api/rag/workspaces');
      const list = res.data?.workspaces || res.data || [];
      setWorkspaces(Array.isArray(list) ? list : []);
    } catch {
      setWorkspaces([]);
    }
  }, []);

  const loadStatus = useCallback(async () => {
    try {
      const res = await api.get('/api/rag/status');
      setRagStatus(res.data);
    } catch {
      setRagStatus(null);
    }
  }, []);

  useEffect(() => {
    loadWorkspaces();
    loadStatus();
  }, [loadWorkspaces, loadStatus]);

  const createWorkspace = async () => {
    if (!newWsName.trim()) return;
    try {
      await api.post('/api/rag/workspaces', { name: newWsName.trim(), description: newWsDesc.trim() });
      toast.success(`Workspace "${newWsName}" created`);
      setNewWsName('');
      setNewWsDesc('');
      loadWorkspaces();
      loadStatus();
    } catch (err) {
      toast.error('Failed to create workspace: ' + (err.response?.data?.error || err.message));
    }
  };

  const addFiles = (fileList) => {
    const newFiles = [...files];
    for (const f of fileList) {
      if (!newFiles.find(x => x.name === f.name && x.size === f.size)) {
        newFiles.push(f);
      }
    }
    setFiles(newFiles);
  };

  const removeFile = (idx) => {
    setFiles(prev => prev.filter((_, i) => i !== idx));
  };

  const handleDrop = (e) => {
    e.preventDefault();
    dropRef.current?.classList.remove('dragover');
    addFiles(e.dataTransfer.files);
  };

  const handleUpload = async () => {
    if (!selectedWs) { toast.warning('Please select a workspace'); return; }
    if (files.length === 0) return;

    const form = new FormData();
    files.forEach(f => form.append('files', f));

    setUploading(true);
    setUploadMsg('Uploading & ingesting...');

    try {
      const res = await api.post(`/api/rag/upload?workspace=${encodeURIComponent(selectedWs)}`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 600000,
      });
      const queued = res.data?.files_queued || [];
      setUploadMsg(`✅ ${queued.length} file(s) ingested successfully`);
      setFiles([]);
      toast.success('Files uploaded and ingested!');
      loadWorkspaces();
      loadStatus();
    } catch (err) {
      setUploadMsg('❌ Upload failed: ' + (err.response?.data?.error || err.message));
      toast.error('Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleQuery = async () => {
    if (!selectedWs) { toast.warning('Please select a workspace'); return; }
    if (!question.trim()) return;

    setQuerying(true);
    setAnswer(null);

    try {
      const res = await api.post('/api/rag/query', {
        question: question.trim(),
        workspace: selectedWs,
        top_k: 6,
      });
      setAnswer(res.data);
    } catch (err) {
      setAnswer({ error: err.response?.data?.error || err.message });
    } finally {
      setQuerying(false);
    }
  };

  const totalDocs = workspaces.reduce((s, w) => s + (w.document_count || 0), 0);
  const totalChunks = workspaces.reduce((s, w) => s + (w.total_chunks || 0), 0);
  const isHealthy = ragStatus?.status === 'healthy';

  return (
    <Layout>
      <div className="knowledge-base">
        {/* Header */}
        <div className="kb-header">
          <h1><FaDatabase style={{ marginRight: 8 }} /> Enterprise Knowledge Base</h1>
          <div className="kb-status">
            <span className={`dot ${isHealthy ? 'ok' : 'err'}`} />
            <span>{isHealthy ? 'RAG Ready' : 'Unavailable'}</span>
            <span>{totalDocs} docs</span>
            <span>{totalChunks} chunks</span>
          </div>
        </div>

        {/* Tabs */}
        <div className="kb-tabs">
          <button className={`kb-tab ${tab === 'workspaces' ? 'active' : ''}`} onClick={() => setTab('workspaces')}>
            🏢 Workspaces
          </button>
          <button className={`kb-tab ${tab === 'upload' ? 'active' : ''}`} onClick={() => setTab('upload')}>
            <FaUpload style={{ marginRight: 4 }} /> Upload Data
          </button>
          <button className={`kb-tab ${tab === 'query' ? 'active' : ''}`} onClick={() => setTab('query')}>
            <FaSearch style={{ marginRight: 4 }} /> Query
          </button>
        </div>

        {/* Workspaces Tab */}
        {tab === 'workspaces' && (
          <div>
            <div className="ws-create-row">
              <input value={newWsName} onChange={e => setNewWsName(e.target.value)} placeholder="Workspace name" />
              <input value={newWsDesc} onChange={e => setNewWsDesc(e.target.value)} placeholder="Description (optional)" />
              <button className="btn btn-primary btn-sm" onClick={createWorkspace} disabled={!newWsName.trim()}>
                <FaPlus /> Create
              </button>
            </div>
            {workspaces.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 40, color: '#888' }}>No workspaces yet. Create one above.</div>
            ) : (
              <div className="ws-grid">
                {workspaces.map(ws => (
                  <div
                    key={ws.workspace}
                    className={`ws-card ${selectedWs === ws.workspace ? 'selected' : ''}`}
                    onClick={() => setSelectedWs(ws.workspace)}
                  >
                    <h4>{ws.workspace}</h4>
                    <p>{ws.description || ''}</p>
                    <div className="meta">
                      <span>📄 {ws.document_count || 0} docs</span>
                      <span>🧩 {ws.total_chunks || 0} chunks</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Upload Tab */}
        {tab === 'upload' && (
          <div>
            <div style={{ marginBottom: 12 }}>
              <label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 4 }}>Target Workspace</label>
              <select
                value={selectedWs}
                onChange={e => setSelectedWs(e.target.value)}
                style={{ padding: '8px 12px', borderRadius: 6, border: '1px solid #ddd', fontSize: 13, width: '100%', maxWidth: 300 }}
              >
                <option value="">Select workspace...</option>
                {workspaces.map(ws => <option key={ws.workspace} value={ws.workspace}>{ws.workspace}</option>)}
              </select>
            </div>

            <div
              ref={dropRef}
              className="upload-zone"
              onClick={() => fileInputRef.current?.click()}
              onDragOver={e => { e.preventDefault(); dropRef.current?.classList.add('dragover'); }}
              onDragLeave={() => dropRef.current?.classList.remove('dragover')}
              onDrop={handleDrop}
            >
              <div className="icon">📁</div>
              <p><strong>Drag & drop files here</strong> or click to browse</p>
              <p className="types">PDF, DOCX, XLSX, CSV, TXT, images, code files, ZIP archives</p>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                hidden
                accept=".pdf,.docx,.xlsx,.xls,.csv,.txt,.md,.json,.xml,.html,.py,.js,.ts,.java,.c,.cpp,.go,.rs,.rb,.php,.png,.jpg,.jpeg,.gif,.webp,.zip,.tar,.gz"
                onChange={e => addFiles(e.target.files)}
              />
            </div>

            {files.length > 0 && (
              <div className="file-list">
                {files.map((f, i) => (
                  <div className="file-item" key={`${f.name}-${i}`}>
                    <div>
                      <div className="name">{f.name}</div>
                      <div className="size">{(f.size / 1024).toFixed(1)} KB</div>
                    </div>
                    <button className="remove" onClick={() => removeFile(i)}><FaTimes /></button>
                  </div>
                ))}
              </div>
            )}

            <div className="upload-actions">
              <button className="btn btn-primary" onClick={handleUpload} disabled={uploading || files.length === 0 || !selectedWs}>
                {uploading ? 'Uploading...' : `Upload ${files.length} file(s)`}
              </button>
              {uploadMsg && <span className="upload-status">{uploadMsg}</span>}
            </div>
          </div>
        )}

        {/* Query Tab */}
        {tab === 'query' && (
          <div>
            <div style={{ marginBottom: 12 }}>
              <label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 4 }}>Workspace</label>
              <select
                value={selectedWs}
                onChange={e => setSelectedWs(e.target.value)}
                style={{ padding: '8px 12px', borderRadius: 6, border: '1px solid #ddd', fontSize: 13, width: '100%', maxWidth: 300 }}
              >
                <option value="">Select workspace...</option>
                {workspaces.map(ws => <option key={ws.workspace} value={ws.workspace}>{ws.workspace}</option>)}
              </select>
            </div>

            <div className="query-row">
              <input
                value={question}
                onChange={e => setQuestion(e.target.value)}
                placeholder="Ask a question about your documents..."
                onKeyDown={e => e.key === 'Enter' && handleQuery()}
              />
              <button className="btn btn-primary" onClick={handleQuery} disabled={querying || !question.trim() || !selectedWs}>
                {querying ? 'Thinking...' : 'Ask'}
              </button>
            </div>

            {answer && (
              <div className="answer-card">
                {answer.error ? (
                  <div className="text" style={{ color: '#dc3545' }}>Error: {answer.error}</div>
                ) : (
                  <>
                    <div className="text">{answer.answer}</div>
                    {answer.sources?.length > 0 && (
                      <div className="sources">
                        <strong>Sources: </strong>
                        {answer.sources.map((s, i) => <span key={i}>{s.doc_name}</span>)}
                      </div>
                    )}
                    <div className="model-info">
                      Model: {answer.model_used || '-'} · {answer.top_k_used || 0} chunks searched
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default KnowledgeBase;
